import { useState, useEffect } from "react";

import { db } from "./firebase";

import { useClinic }
  from "./context/ClinicContext";

import AnatomicalTooth from "./components/dental/AnatomicalTooth";

import {

  evaluarRiesgoPeriodontal,

  generarAlertasMedicas,

  clasificarPeriodonto

} from "./utils/ClinicalEngine";

import {
  sugerirTratamientoPeriodontal
} from "./utils/TreatmentEngine";

import {
  generarSOAPPeriodontal
} from "./utils/SOAPEngine";

import {
  exportarPDF
} from "./utils/PDFEngine";

import {
  doc,
  getDoc,
  setDoc
} from "./access/firestore";

const dientes = [

  "18","17","16","15","14","13","12","11",

  "21","22","23","24","25","26","27","28",

  "48","47","46","45","44","43","42","41",

  "31","32","33","34","35","36","37","38"

];

// =========================
// HELPERS
// =========================

const esSuperior = (d) =>
  parseInt(d) <= 28;

const sitiosPorDiente = (d) => [

  "MB",

  "V",

  "DB",

  "ML",

  esSuperior(d)
    ? "P"
    : "L",

  "DL"

];

// =========================
// COMPONENT
// =========================

export default function PeriodontogramaPanel({
  pacienteId
}) {

  const { clinicId } =
    useClinic();

  const [datos, setDatos] =
    useState({});

  const [bop, setBop] =
    useState({});

  const [movilidad, setMovilidad] =
    useState({});

  const [furca, setFurca] =
    useState({});

  const [
    historiaClinica,
    setHistoriaClinica
  ] = useState({});

  const [
    pacienteData,
    setPacienteData
  ] = useState({});

  const [loading, setLoading] =
    useState(true);

  const [
    dienteActivo,
    setDienteActivo
  ] = useState(null);

  const [
    tempDatos,
    setTempDatos
  ] = useState({});

  const [
    tempBop,
    setTempBop
  ] = useState({});

  const [
    tempMov,
    setTempMov
  ] = useState("");

  const [
    tempFurca,
    setTempFurca
  ] = useState("");

  // =========================
  // LOAD
  // =========================

  useEffect(() => {

    if (
      !pacienteId ||
      !clinicId
    ) {

      setLoading(false);

      return;
    }

    const cargar = async () => {

      try {

        const snap =
          await getDoc(

            doc(
              db,
              "clinics",
              clinicId,
              "pacientes",
              pacienteId
            )

          );

        if (snap.exists()) {

          const paciente =
            snap.data();

          setPacienteData(
            paciente
          );

          setHistoriaClinica(

            paciente
              ?.historiaClinica || {}

          );

          const data =
            snap.data()
              ?.periodonto;

          if (data) {

            setDatos(
              data.datos || {}
            );

            setBop(
              data.bop || {}
            );

            setMovilidad(
              data.movilidad || {}
            );

            setFurca(
              data.furca || {}
            );
          }
        }

      } catch (e) {

        console.error(
          "Error cargando periodontograma:",
          e
        );

      } finally {

        setLoading(false);

      }

    };

    cargar();

  }, [
    pacienteId,
    clinicId
  ]);

  // =========================
  // AUTOSAVE
  // =========================

  useEffect(() => {

    if (
      loading ||
      !pacienteId ||
      !clinicId
    ) return;

    const t = setTimeout(
      async () => {

        try {

          await setDoc(

            doc(
              db,
              "clinics",
              clinicId,
              "pacientes",
              pacienteId
            ),

            {

              periodonto: {

                datos,

                bop,

                movilidad,

                furca

              }

            },

            {
              merge: true
            }

          );

        } catch(err) {

          console.error(
            "Error guardando periodontograma:",
            err
          );

        }

      },

      800
    );

    return () =>
      clearTimeout(t);

  }, [

    datos,

    bop,

    movilidad,

    furca,

    loading,

    pacienteId,

    clinicId

  ]);

  // =========================
  // ABRIR DIENTE
  // =========================

  const abrirDiente = (d) => {

    setDienteActivo(d);

    let temp = {};

    let tempB = {};

    sitiosPorDiente(d)
      .forEach(s => {

        const key =
          `${d}_${s}`;

        temp[s] =
          datos[key] || "";

        tempB[s] =
          bop[key] || false;

      });

    setTempDatos(temp);

    setTempBop(tempB);

    setTempMov(
      movilidad[d] || ""
    );

    setTempFurca(
      furca[d] || ""
    );

  };

  // =========================
  // GUARDAR DIENTE
  // =========================

  const guardarDiente = () => {

    let nuevosDatos = {
      ...datos
    };

    let nuevosBop = {
      ...bop
    };

    sitiosPorDiente(
      dienteActivo
    ).forEach(s => {

      const key =
        `${dienteActivo}_${s}`;

      nuevosDatos[key] =
        tempDatos[s];

      nuevosBop[key] =
        tempBop[s];

    });

    setDatos(nuevosDatos);

    setBop(nuevosBop);

    setMovilidad(prev => ({

      ...prev,

      [dienteActivo]:
        tempMov

    }));

    setFurca(prev => ({

      ...prev,

      [dienteActivo]:
        tempFurca

    }));

    setDienteActivo(null);

  };

  // =========================
  // COLORES
  // =========================

  const coloresDiente = (d) => {
    const colorPorMaximo = (sitios) => {
      const maximo = Math.max(
        ...sitios
          .map((s) => Number(datos[`${d}_${s}`]))
          .filter((v) => v > 0),
        0
      );

      if (!maximo) return "#ffffff";
      if (maximo >= 6) return "#fecaca";
      if (maximo >= 4) return "#fef3c7";
      return "#dcfce7";
    };

    return {
      vestibular: colorPorMaximo(["MB", "V", "DB"]),
      lingual: colorPorMaximo(["ML", esSuperior(d) ? "P" : "L", "DL"]),
    };
  };

  // =========================
  // ANÁLISIS
  // =========================

  const analizar = () => {

    const valores =
      Object.values(datos)

        .map(v => +v)

        .filter(v => v > 0);

    if (!valores.length)
      return null;

    const promedio =

      valores.reduce(
        (a,b) => a+b,
        0
      ) / valores.length;

    const max =
      Math.max(...valores);

    const bopTotal =

      Object.values(bop)
        .filter(v => v)
        .length;

    const bopPorcentaje =

      (
        bopTotal /
        valores.length
      ) * 100;

    const porDiente =

      dientes.map(d => {

        const vest =

          ["MB","V","DB"]

            .map(s =>
              Number(
                datos[
                  `${d}_${s}`
                ]
              )
            )

            .filter(v => v > 0);

        const ling =

          [
            "ML",

            esSuperior(d)
              ? "P"
              : "L",

            "DL"

          ]

            .map(s =>
              Number(
                datos[
                  `${d}_${s}`
                ]
              )
            )

            .filter(v => v > 0);

        if (
          !vest.length &&
          !ling.length
        ) return null;

        return {

          diente: d,

          vest:
            vest.length
              ? Math.max(...vest)
              : 0,

          ling:
            ling.length
              ? Math.max(...ling)
              : 0

        };

      })

      .filter(Boolean);

    return {

      promedio:
        promedio.toFixed(2),

      max,

      bop:
        bopPorcentaje.toFixed(1),

      dx:

        max <= 3

          ? "Salud / Gingivitis"

          : "Periodontitis",

      porDiente

    };

  };

  const resultado =
    analizar();

  const riesgo =
    evaluarRiesgoPeriodontal({

      paciente:
        pacienteData,

      historiaClinica,

      periodonto: {

        datos,
        bop,
        movilidad,
        furca
      }
    });

  const alertas =
    generarAlertasMedicas({

      historiaClinica
    });

  const clasificacion =
    clasificarPeriodonto({

      periodonto: {

        datos
      }
    });

  const tratamiento =
    sugerirTratamientoPeriodontal({

      clasificacion,

      riesgo,

      historiaClinica
    });

  const soap =
    generarSOAPPeriodontal({

      paciente:
        pacienteData,

      historiaClinica,

      clasificacion,

      riesgo,

      tratamiento,

      periodonto: {

        datos,
        bop,
        movilidad,
        furca
      }
    });

  // =========================
  // VALIDACIONES
  // =========================

  if (!pacienteId) {

    return (
      <div>
        Paciente no válido
      </div>
    );

  }

  if (!clinicId) {

    return (
      <div>
        Clínica no válida
      </div>
    );

  }

  if (loading) {

    return (
      <div>
        Cargando periodontograma...
      </div>
    );

  }

  
  // =========================
  // RENDER — INTERFAZ MODERNA
  // =========================

  const ui = {
    card: {
      background: "#ffffff",
      border: "1px solid #e5eaf2",
      borderRadius: 18,
      padding: 20,
      boxShadow: "0 4px 18px rgba(15,23,42,0.035)",
      minWidth: 0,
    },
    label: {
      color: "#64748b",
      fontSize: 12,
      fontWeight: 600,
    },
    heading: {
      margin: 0,
      color: "#172033",
      fontSize: 17,
      fontWeight: 750,
      letterSpacing: "-0.3px",
    },
    button: {
      border: "1px solid #dbe3ef",
      borderRadius: 10,
      padding: "9px 13px",
      background: "#ffffff",
      color: "#334155",
      fontSize: 12,
      fontWeight: 700,
      cursor: "pointer",
    },
  };

  const totalSitios = dientes.reduce(
    (total, d) =>
      total +
      sitiosPorDiente(d).filter(
        (s) => datos[`${d}_${s}`] !== "" &&
          datos[`${d}_${s}`] != null &&
          Number.isFinite(Number(datos[`${d}_${s}`]))
      ).length,
    0
  );

  const dientesRegistrados = dientes.filter((d) =>
    sitiosPorDiente(d).some(
      (s) => datos[`${d}_${s}`] !== "" &&
        datos[`${d}_${s}`] != null
    )
  ).length;

  const totalBop = Object.values(bop).filter(Boolean).length;

  const cerrarDiente = () => setDienteActivo(null);

  const actualizarMedicion = (sitio, valor) => {
    if (valor === "") {
      setTempDatos((prev) => ({ ...prev, [sitio]: "" }));
      return;
    }

    const numero = Number(valor);

    if (!Number.isInteger(numero) || numero < 0 || numero > 15) {
      return;
    }

    setTempDatos((prev) => ({ ...prev, [sitio]: String(numero) }));
  };

  if (!pacienteId) {
    return (
      <div style={{ padding: 24, color: "#64748b" }}>
        Paciente no válido.
      </div>
    );
  }

  if (!clinicId) {
    return (
      <div style={{ padding: 24, color: "#64748b" }}>
        Clínica no válida.
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        minHeight: 240,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        color: "#64748b",
        fontSize: 13,
      }}>
        <div style={{
          width: 32,
          height: 32,
          border: "3px solid #dbeafe",
          borderTopColor: "#2563eb",
          borderRadius: "50%",
          animation: "periodonto-spin 0.8s linear infinite",
        }} />
        Cargando expediente periodontal...
        <style>{`
          @keyframes periodonto-spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: 18,
      width: "100%",
      minWidth: 0,
      color: "#172033",
      fontFamily: "inherit",
    }}>

      {/* CABECERA */}
      <section style={{
        ...ui.card,
        background: "linear-gradient(120deg, #10243e 0%, #174a72 100%)",
        border: "none",
        color: "#ffffff",
        padding: "24px",
        overflow: "hidden",
        position: "relative",
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 18,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 10,
              color: "#bfdbfe",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "1.4px",
              textTransform: "uppercase",
            }}>
              <span style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "#60a5fa",
              }} />
              Expediente clínico
            </div>

            <h2 style={{
              margin: 0,
              fontSize: 25,
              lineHeight: 1.2,
              fontWeight: 800,
              letterSpacing: "-0.7px",
              color: "#ffffff",
            }}>
              Periodontograma
            </h2>

            <p style={{
              margin: "9px 0 0",
              color: "#dbeafe",
              fontSize: 13,
              lineHeight: 1.6,
              maxWidth: 520,
            }}>
              Registro periodontal, sondaje por sitios y evaluación clínica.
            </p>
          </div>

          <div style={{
            padding: "11px 15px",
            borderRadius: 13,
            background: "rgba(255,255,255,0.10)",
            border: "1px solid rgba(255,255,255,0.18)",
            minWidth: 120,
          }}>
            <div style={{
              color: "#bfdbfe",
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.7px",
            }}>
              Dientes registrados
            </div>
            <div style={{
              fontSize: 25,
              fontWeight: 800,
              marginTop: 4,
              color: "#ffffff",
            }}>
              {dientesRegistrados}
              <span style={{
                fontSize: 13,
                color: "#bfdbfe",
                fontWeight: 500,
              }}>
                {" "}/ 32
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* INDICADORES */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
        gap: 12,
      }}>
        {[
          {
            titulo: "Sitios registrados",
            valor: totalSitios,
            detalle: "Mediciones capturadas",
            color: "#2563eb",
            fondo: "#eff6ff",
          },
          {
            titulo: "Profundidad máxima",
            valor: resultado ? `${resultado.max} mm` : "—",
            detalle: "Mayor sondaje registrado",
            color: "#b45309",
            fondo: "#fffbeb",
          },
          {
            titulo: "Sangrado BOP",
            valor: resultado ? `${resultado.bop}%` : "—",
            detalle: `${totalBop} sitios marcados`,
            color: "#dc2626",
            fondo: "#fef2f2",
          },
          {
            titulo: "Evaluación de riesgo",
            valor: riesgo?.riesgo || "Pendiente",
            detalle: `Score: ${riesgo?.score ?? "—"}`,
            color: "#7c3aed",
            fondo: "#f5f3ff",
          },
        ].map((item) => (
          <div key={item.titulo} style={{
            ...ui.card,
            padding: 16,
          }}>
            <div style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: item.fondo,
              color: item.color,
              fontWeight: 900,
              fontSize: 15,
              marginBottom: 13,
            }}>
              {item.titulo === "Sitios registrados" ? "S" :
                item.titulo === "Profundidad máxima" ? "mm" :
                item.titulo === "Sangrado BOP" ? "B" : "R"}
            </div>

            <div style={ui.label}>{item.titulo}</div>

            <div style={{
              fontSize: 23,
              fontWeight: 800,
              letterSpacing: "-0.6px",
              marginTop: 5,
              color: item.color,
              overflowWrap: "anywhere",
            }}>
              {item.valor}
            </div>

            <div style={{
              fontSize: 11,
              color: "#94a3b8",
              marginTop: 5,
            }}>
              {item.detalle}
            </div>
          </div>
        ))}
      </div>

      {/* MAPA DENTAL */}
      <section style={ui.card}>
        <div style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 20,
        }}>
          <div>
            <h3 style={ui.heading}>Registro por diente</h3>
            <p style={{
              margin: "6px 0 0",
              color: "#64748b",
              fontSize: 12,
              lineHeight: 1.6,
            }}>
              Selecciona un diente para registrar los seis sitios periodontales.
            </p>
          </div>

          <div style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 12,
            fontSize: 11,
            color: "#64748b",
          }}>
            {[
              { color: "#ffffff", borde: "#cbd5e1", texto: "Sin datos" },
              { color: "#bbf7d0", borde: "#86efac", texto: "Sondaje ≤ 3 mm" },
              { color: "#fecaca", borde: "#fca5a5", texto: "Sondaje > 3 mm" },
            ].map((leyenda) => (
              <div key={leyenda.texto} style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}>
                <span style={{
                  width: 11,
                  height: 11,
                  borderRadius: 4,
                  background: leyenda.color,
                  border: `1px solid ${leyenda.borde}`,
                }} />
                {leyenda.texto}
              </div>
            ))}
          </div>
        </div>

        <div style={{
          borderRadius: 14,
          background: "#f8fafc",
          border: "1px solid #e9eef5",
          padding: "18px 12px",
          overflowX: "auto",
        }}>
          <div style={{
            minWidth: 680,
          }}>
            <div style={{
              color: "#64748b",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "1px",
              marginBottom: 12,
              textAlign: "center",
            }}>
              MAXILAR · ARCADA SUPERIOR
            </div>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(16, minmax(30px, 1fr))",
              gap: 4,
              alignItems: "center",
            }}>
              {dientes.slice(0, 16).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => abrirDiente(d)}
                  title={`Registrar diente ${d}`}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 5,
                    padding: "7px 2px",
                    borderRadius: 10,
                    border: "1px solid #e2e8f0",
                    background: dienteActivo === d ? "#dbeafe" : "#ffffff",
                    cursor: "pointer",
                    boxShadow: dienteActivo === d
                      ? "0 0 0 2px #60a5fa"
                      : "0 1px 3px rgba(15,23,42,0.04)",
                    minWidth: 0,
                  }}
                >
                  <span style={{
                    color: "#334155",
                    fontWeight: 800,
                    fontSize: 11,
                  }}>
                    {d}
                  </span>
                  <AnatomicalTooth
                    diente={d}
                    numero={d}
                    colors={coloresDiente(d)}
                    compact
                    showNumber={false}
                  />
                  <span style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: "#64748b",
                  }}>
                    {sitiosPorDiente(d).filter(
                      (s) => datos[`${d}_${s}`] !== "" &&
                        datos[`${d}_${s}`] != null
                    ).length}/6
                  </span>
                </button>
              ))}
            </div>

            <div style={{
              height: 1,
              background: "#cbd5e1",
              margin: "18px 0",
            }} />

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(16, minmax(30px, 1fr))",
              gap: 4,
              alignItems: "center",
            }}>
              {dientes.slice(16, 32).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => abrirDiente(d)}
                  title={`Registrar diente ${d}`}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 5,
                    padding: "7px 2px",
                    borderRadius: 10,
                    border: "1px solid #e2e8f0",
                    background: dienteActivo === d ? "#dbeafe" : "#ffffff",
                    cursor: "pointer",
                    boxShadow: dienteActivo === d
                      ? "0 0 0 2px #60a5fa"
                      : "0 1px 3px rgba(15,23,42,0.04)",
                    minWidth: 0,
                  }}
                >
                  <span style={{
                    color: "#334155",
                    fontWeight: 800,
                    fontSize: 11,
                  }}>
                    {d}
                  </span>
                  <AnatomicalTooth
                    diente={d}
                    numero={d}
                    colors={coloresDiente(d)}
                    compact
                    showNumber={false}
                  />
                  <span style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: "#64748b",
                  }}>
                    {sitiosPorDiente(d).filter(
                      (s) => datos[`${d}_${s}`] !== "" &&
                        datos[`${d}_${s}`] != null
                    ).length}/6
                  </span>
                </button>
              ))}
            </div>

            <div style={{
              color: "#64748b",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "1px",
              marginTop: 12,
              textAlign: "center",
            }}>
              MANDÍBULA · ARCADA INFERIOR
            </div>
          </div>
        </div>

        <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          marginTop: 14,
        }}>
          <span style={{
            background: "#eff6ff",
            color: "#1d4ed8",
            borderRadius: 8,
            padding: "7px 10px",
            fontSize: 11,
            fontWeight: 700,
          }}>
            Azul: vestibular
          </span>
          <span style={{
            background: "#f5f3ff",
            color: "#6d28d9",
            borderRadius: 8,
            padding: "7px 10px",
            fontSize: 11,
            fontWeight: 700,
          }}>
            Morado: lingual / palatino
          </span>
          <span style={{
            background: "#fef2f2",
            color: "#b91c1c",
            borderRadius: 8,
            padding: "7px 10px",
            fontSize: 11,
            fontWeight: 700,
          }}>
            BOP: sangrado
          </span>
        </div>
      </section>

      {/* ALERTAS MÉDICAS */}
      {alertas.length > 0 && (
        <section style={{
          ...ui.card,
          borderColor: "#fed7aa",
        }}>
          <h3 style={{
            ...ui.heading,
            marginBottom: 14,
          }}>
            Alertas clínicas
          </h3>

          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}>
            {alertas.map((a, i) => {
              const peligro = a.tipo === "danger";

              return (
                <div key={i} style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                  padding: 14,
                  borderRadius: 12,
                  background: peligro ? "#fef2f2" : "#fffbeb",
                  border: `1px solid ${peligro ? "#fecaca" : "#fde68a"}`,
                }}>
                  <span style={{
                    display: "flex",
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: 9,
                    alignItems: "center",
                    justifyContent: "center",
                    background: peligro ? "#fee2e2" : "#fef3c7",
                    color: peligro ? "#b91c1c" : "#92400e",
                    fontWeight: 900,
                  }}>
                    !
                  </span>

                  <div>
                    <div style={{
                      fontWeight: 800,
                      fontSize: 13,
                      color: peligro ? "#991b1b" : "#92400e",
                    }}>
                      {a.titulo}
                    </div>
                    <div style={{
                      marginTop: 4,
                      fontSize: 12,
                      lineHeight: 1.6,
                      color: "#475569",
                    }}>
                      {a.descripcion}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ANÁLISIS PERIODONTAL */}
      {resultado && (
        <section style={ui.card}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 12,
            marginBottom: 20,
          }}>
            <div>
              <h3 style={ui.heading}>Análisis periodontal</h3>
              <p style={{
                color: "#64748b",
                fontSize: 12,
                margin: "6px 0 0",
              }}>
                Resumen de las mediciones registradas.
              </p>
            </div>

            <div style={{
              borderRadius: 10,
              padding: "9px 13px",
              color: "#ffffff",
              background: riesgo.color,
              fontSize: 12,
              fontWeight: 800,
            }}>
              Riesgo: {riesgo.riesgo} · {riesgo.score}
            </div>
          </div>

          <div style={{
            padding: 16,
            background: resultado.max <= 3 ? "#f0fdf4" : "#fff7ed",
            border: `1px solid ${resultado.max <= 3 ? "#bbf7d0" : "#fed7aa"}`,
            borderRadius: 13,
            marginBottom: 18,
          }}>
            <div style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.8px",
              textTransform: "uppercase",
              color: "#64748b",
            }}>
              Clasificación calculada
            </div>
            <div style={{
              marginTop: 6,
              fontSize: 17,
              fontWeight: 800,
              color: "#172033",
            }}>
              {clasificacion.dx}
            </div>
            <div style={{
              marginTop: 5,
              color: "#64748b",
              fontSize: 12,
            }}>
              Severidad: {clasificacion.severidad}
            </div>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 12,
          }}>
            {[
              {
                label: "Profundidad promedio",
                value: `${resultado.promedio} mm`,
                color: "#2563eb",
              },
              {
                label: "Profundidad máxima",
                value: `${resultado.max} mm`,
                color: "#b45309",
              },
              {
                label: "Sangrado BOP",
                value: `${resultado.bop}%`,
                color: "#dc2626",
              },
            ].map((item) => (
              <div key={item.label} style={{
                background: "#f8fafc",
                border: "1px solid #edf1f6",
                borderRadius: 13,
                padding: 15,
              }}>
                <div style={ui.label}>{item.label}</div>
                <div style={{
                  marginTop: 8,
                  fontSize: 24,
                  fontWeight: 800,
                  color: item.color,
                }}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>

          {riesgo.factores?.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div style={{
                fontSize: 12,
                fontWeight: 800,
                marginBottom: 10,
              }}>
                Factores de riesgo identificados
              </div>
              <div style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}>
                {riesgo.factores.map((f) => (
                  <span key={f} style={{
                    background: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    borderRadius: 9,
                    padding: "7px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#475569",
                  }}>
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* GRÁFICA */}
          <div style={{ marginTop: 24 }}>
            <div style={{
              fontSize: 13,
              fontWeight: 800,
              marginBottom: 6,
            }}>
              Comparación de sondaje por diente
            </div>
            <div style={{
              fontSize: 11,
              color: "#64748b",
              marginBottom: 12,
            }}>
              Valores máximos registrados por cara vestibular y lingual/palatina.
            </div>

            <div style={{
              width: "100%",
              overflowX: "auto",
              border: "1px solid #edf1f6",
              borderRadius: 12,
              padding: 8,
              background: "#fcfdff",
            }}>
              <svg
                width="100%"
                height="210"
                viewBox="0 0 960 210"
                role="img"
                aria-label="Gráfica de sondaje periodontal"
                style={{ minWidth: 600, display: "block" }}
              >
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <g key={n}>
                    <line
                      x1="35"
                      y1={18 + n * 15}
                      x2="950"
                      y2={18 + n * 15}
                      stroke={n === 7 ? "#fecaca" : "#edf1f6"}
                      strokeDasharray={n === 7 ? "4 4" : "0"}
                    />
                    <text
                      x="22"
                      y={22 + n * 15}
                      textAnchor="middle"
                      fontSize="10"
                      fill="#94a3b8"
                    >
                      {10 - n}
                    </text>
                  </g>
                ))}
                
                {(() => {
                  const puntosVestibular = resultado.porDiente
                    .map((d, i) => ({
                      x: 48 + i * (890 / Math.max(1, resultado.porDiente.length - 1)),
                      y: 168 - d.vest * 15,
                      diente: d.diente,
                      valor: d.vest,
                    }));

                  const puntosLingual = resultado.porDiente
                    .map((d, i) => ({
                      x: 48 + i * (890 / Math.max(1, resultado.porDiente.length - 1)),
                      y: 168 - d.ling * 15,
                      diente: d.diente,
                      valor: d.ling,
                    }));

                  return (
                    <>
                      <polyline
                        points={puntosVestibular.map((p) => `${p.x},${p.y}`).join(" ")}
                        fill="none"
                        stroke="#2563eb"
                        strokeWidth="2.5"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                      />

                      <polyline
                        points={puntosLingual.map((p) => `${p.x},${p.y}`).join(" ")}
                        fill="none"
                        stroke="#9333ea"
                        strokeWidth="2.5"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                      />

                      {puntosVestibular.map((p) => (
                        <g key={`vest-${p.diente}`}>
                          <circle cx={p.x} cy={p.y} r="4" fill="#2563eb" />
                          <title>{`Diente ${p.diente}: vestibular ${p.valor} mm`}</title>
                        </g>
                      ))}

                      {puntosLingual.map((p) => (
                        <g key={`ling-${p.diente}`}>
                          <circle cx={p.x} cy={p.y} r="4" fill="#9333ea" />
                          <title>{`Diente ${p.diente}: lingual/palatino ${p.valor} mm`}</title>
                        </g>
                      ))}
                    </>
                  );
                })()}

                <text x="45" y="207" fontSize="9" fill="#64748b">
                  Número dental
                </text>
              </svg>
            </div>

            <div style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              marginTop: 10,
              fontSize: 11,
              color: "#64748b",
            }}>
              <span>
                <span style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#2563eb",
                  marginRight: 6,
                }} />
                Vestibular
              </span>
              <span>
                <span style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#9333ea",
                  marginRight: 6,
                }} />
                Lingual / palatino
              </span>
            </div>
          </div>
        </section>
      )}

      {/* PLAN DE TRATAMIENTO */}
      <section style={ui.card}>
        <div style={{ marginBottom: 18 }}>
          <h3 style={ui.heading}>Plan de tratamiento sugerido</h3>
          <p style={{
            color: "#64748b",
            fontSize: 12,
            lineHeight: 1.6,
            margin: "6px 0 0",
          }}>
            Propuesta generada a partir de la clasificación, los factores de riesgo y los datos clínicos disponibles. Requiere validación del odontólogo.
          </p>
        </div>

        {tratamiento.plan.map((fase, i) => (
          <div key={i} style={{
            display: "flex",
            gap: 13,
            marginBottom: i === tratamiento.plan.length - 1 ? 0 : 12,
            padding: 15,
            borderRadius: 13,
            border: "1px solid #e5eaf2",
            background: "#ffffff",
          }}>
            <div style={{
              width: 34,
              height: 34,
              flexShrink: 0,
              borderRadius: 10,
              background: "#eff6ff",
              color: "#2563eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 900,
              fontSize: 13,
            }}>
              {i + 1}
            </div>

            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontSize: 13,
                fontWeight: 800,
                marginBottom: 9,
              }}>
                {fase.fase}
              </div>

              <div style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}>
                {fase.procedimientos.map((p, j) => (
                  <div key={j} style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    color: "#475569",
                    fontSize: 12,
                    lineHeight: 1.6,
                  }}>
                    <span style={{
                      color: "#2563eb",
                      fontWeight: 900,
                    }}>
                      •
                    </span>
                    <span>{p}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* NOTA SOAP */}
      <section style={ui.card}>
        <div style={{ marginBottom: 20 }}>
          <h3 style={ui.heading}>Nota clínica SOAP</h3>
          <p style={{
            color: "#64748b",
            fontSize: 12,
            margin: "6px 0 0",
          }}>
            Resumen clínico organizado por componentes de la nota.
          </p>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
          gap: 12,
        }}>
          {[
            {
              letra: "S",
              titulo: "Subjetivo",
              color: "#2563eb",
              fondo: "#eff6ff",
              items: soap.subjetivo,
            },
            {
              letra: "O",
              titulo: "Objetivo",
              color: "#0891b2",
              fondo: "#ecfeff",
              items: soap.objetivo,
            },
            {
              letra: "A",
              titulo: "Análisis",
              color: "#7c3aed",
              fondo: "#f5f3ff",
              items: soap.analisis,
            },
            {
              letra: "P",
              titulo: "Plan",
              color: "#059669",
              fondo: "#ecfdf5",
              items: soap.plan,
            },
          ].map((seccion) => (
            <div key={seccion.letra} style={{
              border: "1px solid #e5eaf2",
              borderRadius: 13,
              padding: 15,
              minWidth: 0,
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 13,
              }}>
                <span style={{
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: seccion.fondo,
                  color: seccion.color,
                  fontWeight: 900,
                }}>
                  {seccion.letra}
                </span>
                <span style={{
                  fontSize: 13,
                  fontWeight: 800,
                }}>
                  {seccion.titulo}
                </span>
              </div>

              <div style={{
                display: "flex",
                flexDirection: "column",
                gap: 9,
              }}>
                {seccion.items.map((item, i) => (
                  <div key={i} style={{
                    color: "#475569",
                    fontSize: 12,
                    lineHeight: 1.65,
                    overflowWrap: "anywhere",
                  }}>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* EXPORTACIÓN EXISTENTE */}
      <div style={{
        display: "flex",
        justifyContent: "flex-end",
      }}>
        <button
          type="button"
          onClick={() =>
            exportarPDF({
              elementId: "periodonto-report",
              nombre: `periodontograma_${pacienteId}`,
            })
          }
          style={{
            border: "none",
            borderRadius: 11,
            padding: "12px 18px",
            background: "#172033",
            color: "#ffffff",
            fontWeight: 750,
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          Exportar PDF
        </button>
      </div>

      {/* MODAL DE REGISTRO */}
      {dienteActivo && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) cerrarDiente();
          }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 999999,
            background: "rgba(15,23,42,0.58)",
            backdropFilter: "blur(5px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 12,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Registro periodontal del diente ${dienteActivo}`}
            style={{
              width: 540,
              maxWidth: "100%",
              maxHeight: "92vh",
              overflowY: "auto",
              background: "#ffffff",
              borderRadius: 20,
              boxShadow: "0 25px 80px rgba(0,0,0,0.25)",
            }}
          >
            <div style={{
              padding: 20,
              borderBottom: "1px solid #e5eaf2",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: "#ffffff",
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 13,
              }}>
                <div style={{
                  width: 54,
                  height: 54,
                  borderRadius: 14,
                  background: "#f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <AnatomicalTooth
                    numero={dienteActivo}
                    colors={coloresDiente(dienteActivo)}
                    compact
                    showNumber={false}
                  />
                </div>

                <div>
                  <div style={{
                    color: "#64748b",
                    fontSize: 10,
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                  }}>
                    Registro periodontal
                  </div>
                  <h3 style={{
                    margin: "4px 0 0",
                    fontSize: 19,
                    fontWeight: 800,
                    color: "#172033",
                  }}>
                    Diente {dienteActivo}
                  </h3>
                </div>
              </div>

              <button
                type="button"
                onClick={cerrarDiente}
                aria-label="Cerrar"
                style={{
                  width: 34,
                  height: 34,
                  border: "1px solid #e2e8f0",
                  borderRadius: 10,
                  background: "#ffffff",
                  color: "#64748b",
                  fontSize: 20,
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: 20 }}>
              <div style={{
                fontSize: 13,
                fontWeight: 800,
                marginBottom: 5,
              }}>
                Profundidad de sondaje
              </div>
              <div style={{
                fontSize: 11,
                color: "#64748b",
                marginBottom: 16,
                lineHeight: 1.6,
              }}>
                Registra la medición en milímetros para cada sitio. Marca el punto rojo cuando exista sangrado al sondaje.
              </div>

              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 9,
              }}>
                {sitiosPorDiente(dienteActivo).map((s) => {
                  const posterior = ["MB", "V", "DB"].includes(s);
                  const valor = tempDatos[s] ?? "";

                  return (
                    <div key={s} style={{
                      padding: 10,
                      border: "1px solid #e5eaf2",
                      borderRadius: 12,
                      background: "#fbfcfe",
                      minWidth: 0,
                    }}>
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 4,
                        marginBottom: 9,
                      }}>
                        <span style={{
                          fontSize: 11,
                          fontWeight: 800,
                          color: posterior ? "#2563eb" : "#7c3aed",
                        }}>
                          {s}
                        </span>

                        <button
                          type="button"
                          title={tempBop[s] ? "Quitar sangrado" : "Marcar sangrado"}
                          aria-label={tempBop[s] ? `Quitar BOP ${s}` : `Marcar BOP ${s}`}
                          onClick={() =>
                            setTempBop((prev) => ({
                              ...prev,
                              [s]: !prev[s],
                            }))
                          }
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            border: tempBop[s] ? "2px solid #dc2626" : "1px solid #cbd5e1",
                            background: tempBop[s] ? "#ef4444" : "#ffffff",
                            cursor: "pointer",
                            padding: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {tempBop[s] && (
                            <span style={{
                              width: 5,
                              height: 5,
                              borderRadius: "50%",
                              background: "#ffffff",
                            }} />
                          )}
                        </button>
                      </div>

                      <input
                        type="number"
                        min="0"
                        max="15"
                        step="1"
                        inputMode="numeric"
                        aria-label={`Sondaje ${s}`}
                        value={valor}
                        onChange={(e) =>
                          actualizarMedicion(s, e.target.value)
                        }
                        placeholder="—"
                        style={{
                          boxSizing: "border-box",
                          width: "100%",
                          height: 43,
                          borderRadius: 9,
                          border: `1px solid ${
                            Number(valor) > 3 ? "#fca5a5" : "#dbe3ef"
                          }`,
                          background: Number(valor) > 3
                            ? "#fef2f2"
                            : "#ffffff",
                          color: Number(valor) > 3
                            ? "#b91c1c"
                            : "#172033",
                          fontSize: 19,
                          fontWeight: 800,
                          textAlign: "center",
                          outlineColor: "#2563eb",
                        }}
                      />

                      <div style={{
                        marginTop: 6,
                        textAlign: "center",
                        fontSize: 9,
                        color: "#94a3b8",
                      }}>
                        mm · BOP
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{
                marginTop: 22,
                paddingTop: 18,
                borderTop: "1px solid #e5eaf2",
              }}>
                <div style={{
                  fontSize: 13,
                  fontWeight: 800,
                  marginBottom: 10,
                }}>
                  Movilidad dental
                </div>

                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: 8,
                }}>
                  {[0, 1, 2, 3].map((n) => {
                    const seleccionado = String(tempMov) === String(n);

                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setTempMov(n)}
                        style={{
                          borderRadius: 10,
                          border: `1px solid ${seleccionado ? "#2563eb" : "#e2e8f0"}`,
                          background: seleccionado ? "#eff6ff" : "#ffffff",
                          color: seleccionado ? "#1d4ed8" : "#475569",
                          padding: "11px 5px",
                          fontWeight: 800,
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ fontSize: 17 }}>{n}</div>
                        <div style={{
                          marginTop: 3,
                          fontSize: 9,
                          fontWeight: 600,
                        }}>
                          {["Sin movilidad", "Grado I", "Grado II", "Grado III"][n]}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ marginTop: 20 }}>
                <div style={{
                  fontSize: 13,
                  fontWeight: 800,
                  marginBottom: 10,
                }}>
                  Compromiso de furca
                </div>

                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: 8,
                }}>
                  {["0", "I", "II", "III"].map((f) => {
                    const seleccionado = String(tempFurca) === f;

                    return (
                      <button
                        key={f}
                        type="button"
                        onClick={() => setTempFurca(f)}
                        style={{
                          borderRadius: 10,
                          border: `1px solid ${seleccionado ? "#7c3aed" : "#e2e8f0"}`,
                          background: seleccionado ? "#f5f3ff" : "#ffffff",
                          color: seleccionado ? "#6d28d9" : "#475569",
                          padding: "11px 5px",
                          fontWeight: 800,
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ fontSize: 17 }}>{f}</div>
                        <div style={{
                          marginTop: 3,
                          fontSize: 9,
                          fontWeight: 600,
                        }}>
                          {["Sin furca", "Grado I", "Grado II", "Grado III"][
                            ["0", "I", "II", "III"].indexOf(f)
                          ]}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={{
              display: "flex",
              gap: 10,
              padding: 18,
              borderTop: "1px solid #e5eaf2",
              background: "#fbfcfe",
              borderRadius: "0 0 20px 20px",
            }}>
              <button
                type="button"
                onClick={cerrarDiente}
                style={{
                  ...ui.button,
                  flex: 1,
                  padding: 12,
                }}
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={guardarDiente}
                style={{
                  flex: 1,
                  padding: 12,
                  borderRadius: 10,
                  border: "1px solid #2563eb",
                  background: "#2563eb",
                  color: "#ffffff",
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Guardar mediciones
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONTENIDO QUE UTILIZA EL EXPORTADOR ACTUAL */}
      <div
        id="periodonto-report"
        style={{
          position: "absolute",
          left: "-100000px",
          top: 0,
          width: 800,
          background: "#ffffff",
          color: "#172033",
          padding: 20,
        }}
      >
        <h2>Reporte periodontal</h2>
        <p>Paciente: {pacienteData?.nombre || ""} {pacienteData?.apellidos || ""}</p>
        <p>Fecha: {new Date().toLocaleDateString("es-MX")}</p>

        <h3>Mediciones por diente</h3>
        <table style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 10,
        }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #ddd", padding: 5 }}>Diente</th>
              {["MB", "V", "DB", "ML", esSuperior("18") ? "P" : "L", "DL"].map((s) => (
                <th key={s} style={{ border: "1px solid #ddd", padding: 5 }}>{s}</th>
              ))}
              <th style={{ border: "1px solid #ddd", padding: 5 }}>Mov.</th>
              <th style={{ border: "1px solid #ddd", padding: 5 }}>Furca</th>
            </tr>
          </thead>
          <tbody>
            {dientes.map((d) => (
              <tr key={d}>
                <td style={{ border: "1px solid #ddd", padding: 5 }}>{d}</td>
                {sitiosPorDiente(d).map((s) => (
                  <td key={s} style={{ border: "1px solid #ddd", padding: 5 }}>
                    {datos[`${d}_${s}`] ?? ""}
                    {bop[`${d}_${s}`] ? " *" : ""}
                  </td>
                ))}
                <td style={{ border: "1px solid #ddd", padding: 5 }}>{movilidad[d] ?? ""}</td>
                <td style={{ border: "1px solid #ddd", padding: 5 }}>{furca[d] ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {resultado && (
          <>
            <h3>Resumen clínico</h3>
            <p>Promedio: {resultado.promedio} mm</p>
            <p>Profundidad máxima: {resultado.max} mm</p>
            <p>BOP: {resultado.bop}%</p>
            <p>Clasificación: {clasificacion.dx}</p>
            <p>Severidad: {clasificacion.severidad}</p>
            <p>Riesgo: {riesgo.riesgo}</p>
          </>
        )}
      </div>
    </div>
  );
}

